import { queriesPayeeEngagements } from "@query";
import {
  useWSModalCustom,
  WSDataItemProps,
  WSElementColorsType
} from "@wingspanhq/fe-component-library";
import {
  RequirementDataSourceType,
  RequirementType
} from "@wingspanhq/wingspan-api-sdk";
import { WSQueryCache, WSQueryKey } from "@ws-react-query";
import { useCallback, useMemo } from "react";

import { QUERY_EMPLOYEE_TAX_FORMS_CHECKLIST } from "../../query/employeeTaxForms/keys";
import { DataItemProps } from "../../shared/components/DataView";
import { IS_LOCAL_ENV } from "../../shared/constants/environment";
import { selectorSearchRowName } from "../../shared/selectors/selectorSearchRowName";
// Registries are imported from their modules directly, NOT the `@query`
// barrel — a barrel import inside src/modules/Requirements/** closes a module
// cycle (see ./dependencies.ts).
import { queriesAuditLogs } from "../../v3/query/queriesAuditLogs";
import { queriesBackgroundChecks } from "../../v3/query/queriesBackgroundChecks";
import { queriesBiometricIdentityVerificationRequests } from "../../v3/query/queriesBiometricIdentityVerificationRequests";
import { queriesInsuranceMonitors } from "../../v3/query/queriesInsuranceMonitors";
import { queriesNotifications } from "../../v3/query/queriesNotifications";
import { queriesPayeeRows } from "../../v3/query/queriesPayeeRows";
import { queriesPayerEngagements } from "../../v3/query/queriesPayerEngagements";
import { queriesPayerRows } from "../../v3/query/queriesPayerRows";
import { queriesRequirements } from "../../v3/query/queriesRequirements";
import { queriesSharedFileRequests } from "../../v3/query/queriesSharedFileRequests";
import { queriesSignatureRequests } from "../../v3/query/queriesSignatureRequests";
import { QUERY_ALL_ENGAGEMENTS_BY_PAYER_IDS } from "../Onboarding/queries/useQueryAllPayersWithEngagements";
import { getRequirementDefinitionDataItemProps } from "../RequirementDefinitions/utils";

import { ModalRequirementHistory } from "./components/ModalRequirementHistory";
import { RequirementVersionLabel } from "./components/RequirementVersionLabel";
import { extendRequirementOnboardingToolbarActions } from "./helpers/extendRequirementOnboardingToolbarActions";
import { extendRequirementStatusCell } from "./helpers/extendRerquirementStatusCell";
import { getRequirementExpirationWidget } from "./helpers/getRequirementExpirationWidget";
import { getRequirementHistoryWidget } from "./helpers/getRequirementHistoryWidget";
import { getRequirementOverrideExpirationAlert } from "./helpers/getRequirementOverrideExpirationAlert";
import { getRequirementOverrideStatusAlert } from "./helpers/getRequirementOverrideStatusAlert";
import { getRequirementProgressWidget } from "./helpers/getRequirementProgressWidget";
import { useRequirementDefaultActions } from "./hooks/useRequirementDefaultActions";
import { useBackgroundChecksRequirementTableConfig } from "./modules/BackgroundChecks";
import { useEmployeeTaxFormsRequirementTableConfig } from "./modules/EmployeeTaxForms";
import {
  isEmployeeTaxFormsRequirementType,
  REQUIREMENT_TYPE_EMPLOYEE_TAX_FORMS
} from "./modules/EmployeeTaxForms/constants";
import { useIdentityVerificationRequirementConfig } from "./modules/IdentityVerification";
import { useInsuranceCoverageRequirementTableConfig } from "./modules/InsuranceCoverage";
import { selectorPolicyTypesFromMonitor } from "./modules/InsuranceCoverage/selectors/selectorPolicyTypesFromMonitor";
import { usePayoutMethodRequirementTableConfig } from "./modules/PayoutMethod";
import { useRegistrationRequirementTableConfig } from "./modules/Registration";
import { useSharedFileRequirementTableConfig } from "./modules/SharedFile";
import { useSignatureRequirementTableConfig } from "./modules/Signature";
import { useTaxVerificationRequirementTableConfig } from "./modules/TaxVerification";
import { selectorRequirementEngagementsSize } from "./selectors/selectorRequirementEngagementsSize";
import { selectorRequirementsConfigFetchState } from "./selectors/selectorRequirementsConfigFetchState";
import {
  ContextItem,
  isSupportedRequirementType,
  RequirementAction,
  RequirementContextType,
  RequirementStatusViewProps,
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig,
  TableCellProps
} from "./types";
import {
  ALL_REQUIREMENT_EVENTS,
  getRequirementPayerDataItemProps,
  getRequirementPayerTableCellProps,
  RequirementEvent
} from "./utils";

export type RequirementsConfigParams = {
  requirements: Array<RequirementTableItemData>;
  contextType: RequirementContextType;
  context?: ContextItem;
};

export type RequirementsConfigResult = ReturnType<typeof useRequirementsConfig>;

/**
 * Cell fallback for requirement types the module has no config for (the SDK
 * enum members outside `SupportedRequirementType`).
 */
const emptyCell: TableCellProps = { text: "--" };

export const useRequirementsConfig = (props: RequirementsConfigParams) => {
  const { contextType, context = {}, requirements } = props;
  const modalHistory = useWSModalCustom(ModalRequirementHistory);

  const callback: RequirementTableCallback = useCallback(
    (eventName, requirement) => {
      const eventsWithNoSideEffects: RequirementEvent[] = [
        ALL_REQUIREMENT_EVENTS.DownloadAsPayee,
        ALL_REQUIREMENT_EVENTS.DownloadAsPayer
      ];

      if (!eventsWithNoSideEffects.includes(eventName)) {
        const listOfQueries: WSQueryKey[] = [];
        const addToInvalidations = (key: WSQueryKey) => listOfQueries.push(key);

        if (requirement.dataSource) {
          const { type: dataSourceType, id: dataSourceId } =
            requirement.dataSource;

          switch (dataSourceType) {
            case RequirementDataSourceType.SIGNATURE_REQUEST:
              addToInvalidations(
                queriesSignatureRequests.getSignatureRequest.getQueryKey({
                  requestId: dataSourceId
                })
              );
              break;
            case RequirementDataSourceType.SHARED_FILE_REQUEST:
              addToInvalidations(
                queriesSharedFileRequests.getSharedFileRequest.getQueryKey({
                  sharedFileRequestId: dataSourceId
                })
              );
              break;
            case RequirementDataSourceType.BACKGROUND_CHECK:
              addToInvalidations(
                queriesBackgroundChecks.getBackgroundCheck.getQueryKey({
                  backgroundCheckId: dataSourceId
                })
              );
              break;
            case RequirementDataSourceType.BIOMETRIC_IDENTITY_VERIFICATION:
              addToInvalidations(
                queriesBiometricIdentityVerificationRequests.getBiometricIdentityVerificationRequest.getQueryKey(
                  {
                    biometricIdentityVerificationRequestId: dataSourceId
                  }
                )
              );
              break;
            case RequirementDataSourceType.INSURANCE_MONITOR:
              addToInvalidations(
                queriesInsuranceMonitors.getInsuranceMonitor.getQueryKey({
                  monitorId: dataSourceId
                })
              );
              break;
          }
        }

        // EmployeeTaxForms is keyed by requirement TYPE, not a data-source
        // type the SDK enum knows: dataSourceId is the payerPayeeEngagementId
        // — the checklist query is keyed by it as the engagement context.
        if (isEmployeeTaxFormsRequirementType(requirement.type)) {
          addToInvalidations([
            QUERY_EMPLOYEE_TAX_FORMS_CHECKLIST,
            requirement.dataSourceId
          ]);
        }

        addToInvalidations(queriesNotifications.listNotifications.queryKeyBase);
        addToInvalidations(
          queriesRequirements.listPayeeRequirements.queryKeyBase
        );
        addToInvalidations(
          queriesRequirements.getPayeeRequirement.getQueryKey({
            requirementId: requirement.id!
          })
        );

        addToInvalidations(queriesPayerRows.searchPayerRows.queryKeyBase);
        addToInvalidations(queriesPayeeRows.searchPayeeRows.queryKeyBase);

        addToInvalidations(
          queriesPayerEngagements.getPayerEngagement.queryKeyBase
        );
        addToInvalidations(
          queriesPayerEngagements.listPayerEngagements.queryKeyBase
        );
        addToInvalidations([QUERY_ALL_ENGAGEMENTS_BY_PAYER_IDS]);
        addToInvalidations([
          queriesPayeeEngagements.listPayeeEngagements.queryKeyBase
        ]);
        addToInvalidations([
          queriesPayeeEngagements.getPayeeEngagement.queryKeyBase
        ]);

        addToInvalidations(queriesAuditLogs.listAuditLogs.queryKeyBase);

        if (IS_LOCAL_ENV) {
          console.log(
            "Invalidating queries",
            JSON.stringify(listOfQueries, null, 2)
          );
        }

        listOfQueries.forEach(key => WSQueryCache.invalidateQueries(key));
      }
    },
    []
  );

  const requirementDefaultActions = useRequirementDefaultActions(
    contextType,
    callback
  );

  const signatureConfig = useSignatureRequirementTableConfig(
    callback,
    requirements
  );
  const sharedFileConfig = useSharedFileRequirementTableConfig(
    callback,
    requirements
  );
  const backgroundCheckConfig = useBackgroundChecksRequirementTableConfig(
    callback,
    requirements
  );
  const identityVerificationConfig = useIdentityVerificationRequirementConfig(
    callback,
    requirements
  );
  const insuranceCoverageConfig = useInsuranceCoverageRequirementTableConfig(
    callback,
    requirements
  );
  const registrationConfig = useRegistrationRequirementTableConfig(
    callback,
    requirements
  );
  const taxVerificationConfig = useTaxVerificationRequirementTableConfig(
    callback,
    requirements
  );
  const payoutMethodConfig = usePayoutMethodRequirementTableConfig(
    callback,
    requirements
  );
  const employeeTaxFormsConfig = useEmployeeTaxFormsRequirementTableConfig(
    callback,
    requirements
  );

  const config: RequirementTableRowConfig = {
    [RequirementType.SIGNATURE]: signatureConfig,
    [RequirementType.DOCUMENT_UPLOAD]: sharedFileConfig,
    [RequirementType.BACKGROUND_CHECK]: backgroundCheckConfig,
    [RequirementType.BIOMETRIC_IDENTITY_VERIFICATION]:
      identityVerificationConfig,
    [RequirementType.INSURANCE_COVERAGE]: insuranceCoverageConfig,
    [RequirementType.REGISTRATION]: registrationConfig,
    [RequirementType.TAX_VERIFICATION]: taxVerificationConfig,
    [RequirementType.PAYOUT_METHOD]: payoutMethodConfig,
    [REQUIREMENT_TYPE_EMPLOYEE_TAX_FORMS]: employeeTaxFormsConfig
  };

  /**
   * The config record only covers `SupportedRequirementType`; instances of the
   * other SDK enum members resolve to `undefined` and every accessor below
   * falls back to a safe default.
   */
  const getTypeConfig = (item: RequirementTableItemData) =>
    isSupportedRequirementType(item.type) ? config[item.type] : undefined;

  /**
   * Centralized client lookup for every requirement on screen, so the "Clients"
   * cell/data item can name the payer behind each one.
   *
   * Keyed by the requirement's payer *account* id — the only payer identity a
   * `PayeeRequirement` carries. That is deliberately not a `Payer.id` (which is
   * the composite `<payerAccountId>_<payeeAccountId>` for a linked client), so
   * this resolves through the v3 payer search rows, whose `payerAccountId` is the
   * matching field. The retired v1 `/search/payer-row` this replaced was rejected
   * outright as `NotMigrated`, which is why the column rendered empty.
   */
  const payerAccountIds = useMemo(
    () => [
      ...new Set(
        requirements
          .filter(req => !!req.payerAccountId)
          .map(req => req.payerAccountId!)
      )
    ],
    [requirements]
  );
  const queryPayerRows = queriesPayerRows.searchPayerRows.useQueryAll(
    { filter: { payerAccountId: { anyOf: payerAccountIds } } },
    { enabled: payerAccountIds.length > 0 }
  );
  const payerRowByAccountId = useMemo(
    () =>
      new Map(
        (queryPayerRows.flatData ?? [])
          .filter(row => !!row.payerAccountId)
          .map(row => [row.payerAccountId!, row])
      ),
    [queryPayerRows.flatData]
  );
  const getPayerRow = (payerAccountId: string | undefined) =>
    payerAccountId ? payerRowByAccountId.get(payerAccountId) : undefined;

  const { isLoading, isFetching } = selectorRequirementsConfigFetchState({
    typeConfigs: Object.values(config),
    payerRows: {
      // A disabled query reports `isLoading` forever — only let the payer lookup
      // gate on loading when it actually has ids to resolve.
      isEnabled: payerAccountIds.length > 0,
      isLoading: queryPayerRows.isLoading,
      isFetching: queryPayerRows.isFetching
    }
  });
  const dataUpdatedAt = Object.values(config)
    .map(({ dataUpdatedAt }) => dataUpdatedAt)
    .join(", ");

  const getStatusCellProps = (item: RequirementTableItemData) => {
    const statusCell =
      getTypeConfig(item)?.statusCell[contextType](item, context) ?? emptyCell;

    return extendRequirementStatusCell(statusCell, item, contextType);
  };

  const getToolbarActions = (requirement: RequirementTableItemData) => {
    const typeConfig = getTypeConfig(requirement);

    const customActions =
      typeConfig?.toolbarActions?.[contextType](requirement, context) || [];

    const dataSource = typeConfig?.getAdditionalData?.(requirement, context);

    const status = (typeConfig?.statusCell[contextType](requirement, context) ??
      emptyCell) as RequirementStatusViewProps;

    const { extendToolbarActions } = requirementDefaultActions(
      requirement,
      context,
      dataSource,
      status
    );

    return extendToolbarActions(customActions);
  };

  return {
    isActionRequired: (item: RequirementTableItemData) => {
      return getTypeConfig(item)?.isActionRequired?.[contextType](
        item,
        context
      );
    },
    getRequirementDetailsWidgets(item: RequirementTableItemData) {
      return getTypeConfig(item)?.requirementDetailsWidgets?.[contextType](
        item,
        context
      );
    },
    getRequirementProgressSteps(item: RequirementTableItemData) {
      return (
        getTypeConfig(item)?.requirementDetailsProgressWidget?.[contextType](
          item,
          context
        ) ?? []
      );
    },
    getRequirementDetailsProgressWidget(
      item: RequirementTableItemData,
      showTitle: boolean = true
    ) {
      const steps = getTypeConfig(item)?.requirementDetailsProgressWidget?.[
        contextType
      ](item, context);

      return getRequirementProgressWidget(steps, item, showTitle);
    },
    getRequirementExpirationWidget(item: RequirementTableItemData) {
      return getRequirementExpirationWidget(item);
    },
    getRequirementDetailsHistoryWidget(item: RequirementTableItemData) {
      return getRequirementHistoryWidget(item);
    },
    getRequirementInstructionsWidgets(item: RequirementTableItemData) {
      return getTypeConfig(item)?.requirementInstructionsWidgets?.[contextType](
        item,
        context
      );
    },
    getRequirementInstructions(item: RequirementTableItemData) {
      return getTypeConfig(item)?.requirementInstructions?.[contextType](
        item,
        context
      );
    },
    getRequirementName(item: RequirementTableItemData) {
      return getTypeConfig(item)?.requirementName?.[contextType](item, context);
    },
    getToolbarActions,
    getOnboardingToolbarActions(item: RequirementTableItemData) {
      return (onClose: () => void) => {
        let actions: RequirementAction[] = [];

        const isMainActionActive = (action: RequirementAction) =>
          action.mainAction &&
          action.visible !== false &&
          action.hidden !== true &&
          action.noOnboarding !== true &&
          action.buttonKind;

        const originalActions = getToolbarActions(item);

        actions = extendRequirementOnboardingToolbarActions(
          item,
          originalActions,
          onClose
        );

        const isMainActionExist = actions.some(isMainActionActive);
        const activeActions = actions.filter(
          action =>
            action.noOnboarding !== true &&
            action.visible !== false &&
            action.hidden !== true
        );

        return activeActions
          .map((action, index, all) => ({
            ...action,
            buttonKind: action.buttonKind
              ? isMainActionExist
                ? isMainActionActive(action)
                  ? action.buttonKind
                  : "Link"
                : action.buttonKind
              : undefined
          }))
          .map(action =>
            isLoading
              ? {
                  ...action,
                  visible: false
                }
              : action
          ) as RequirementAction[];
      };
    },
    getRequirementStatusAlert(requirement: RequirementTableItemData) {
      const approvalFlowAlert = getRequirementOverrideStatusAlert({
        requirement,
        contextType,
        onOpenHistory() {
          modalHistory.open({
            requirement
          });
        }
      });

      if (approvalFlowAlert) {
        return approvalFlowAlert;
      }

      return getTypeConfig(requirement)?.requirementStatusAlert?.[contextType](
        requirement,
        context
      );
    },
    getRequirementExpirationAlerts(requirement: RequirementTableItemData) {
      const statusCell =
        getTypeConfig(requirement)?.statusCell[contextType](
          requirement,
          context
        ) ?? emptyCell;

      const expirationAlerts = getRequirementOverrideExpirationAlert({
        requirement,
        contextType,
        currentStatus: {
          pill: statusCell.pill!,
          secondaryText: statusCell.secondaryText as string,
          secondaryTextColor:
            statusCell.secondaryTextColor as WSElementColorsType
        }
      });

      return expirationAlerts;
    },
    getRequirementBasicInformation(
      item: RequirementTableItemData
    ): DataItemProps[] {
      const customBasicInfo = getTypeConfig(
        item
      )?.requirementBasicInformation?.[contextType](item, context);

      if (customBasicInfo) {
        return customBasicInfo;
      }

      const statusProps = getStatusCellProps(item);

      const dataSource = getTypeConfig(item)?.getAdditionalData?.(
        item,
        context
      );

      // For Payer context, show "Clients" with payer info
      // For Payee context, show "Engagements" count
      const clientOrEngagementItem =
        contextType === RequirementContextType.Payer
          ? getRequirementPayerDataItemProps(
              item,
              getPayerRow(item.payerAccountId)
            )
          : {
              label: "Engagements",
              value: selectorRequirementEngagementsSize(item)
            };

      return [
        getRequirementDefinitionDataItemProps({
          type: item.type!,
          contextType:
            dataSource && "contextType" in dataSource
              ? dataSource.contextType
              : undefined,
          policyType:
            dataSource && "configurationId" in dataSource
              ? selectorPolicyTypesFromMonitor(dataSource)
              : undefined
        }),
        clientOrEngagementItem,
        {
          label: {
            text: "Status"
          },
          value: "",
          hideValue: true,
          ...(statusProps as WSDataItemProps)
        },
        ...(item.eligibilityRequirementVersion &&
        item.eligibilityRequirementVersion > 1 &&
        contextType === RequirementContextType.Payee
          ? [
              {
                label: {
                  text: "Version"
                },
                value: "",
                hideValue: true,
                children: (
                  <RequirementVersionLabel
                    requirementDefinitionId={item.requirementDefinitionId!}
                    size="M"
                    version={item.eligibilityRequirementVersion}
                  />
                )
              }
            ]
          : [])
      ];
    },
    getRequirementDetails(item: RequirementTableItemData) {
      return getTypeConfig(item)?.requirementDetails?.[contextType](
        item,
        context
      );
    },
    getRequirementAdditionalContent(item: RequirementTableItemData) {
      return getTypeConfig(item)?.requirementAdditionalContent?.[contextType](
        item,
        context
      );
    },
    getNameCellProps(item: RequirementTableItemData) {
      const baseNameProps =
        getTypeConfig(item)?.nameCell[contextType](item, context) ?? emptyCell;

      return {
        ...baseNameProps,
        secondaryText:
          contextType === RequirementContextType.Payee ? (
            <RequirementVersionLabel
              defaultSecondaryText={baseNameProps.secondaryText}
              requirementDefinitionId={item.requirementDefinitionId!}
              version={item.eligibilityRequirementVersion}
            />
          ) : undefined
      } as TableCellProps;
    },
    getTypeCellProps(item: RequirementTableItemData) {
      return (
        getTypeConfig(item)?.typeCell[contextType](item, context) ?? emptyCell
      );
    },
    getStatusCellProps(item: RequirementTableItemData) {
      return getStatusCellProps(item);
    },
    getClientsCellProps(item: RequirementTableItemData) {
      const customClientsCellProps = getTypeConfig(item)?.clientsCell?.[
        contextType
      ](item, context);

      if (customClientsCellProps) {
        return customClientsCellProps;
      }

      // Default implementation
      const payer = getPayerRow(item.payerAccountId);
      return getRequirementPayerTableCellProps(item, payer);
    },
    getEngagementsCellProps(item: RequirementTableItemData) {
      return getTypeConfig(item)?.engagementsCell?.[contextType](item, context);
    },
    renderRowMenuActions(item: RequirementTableItemData) {
      return getToolbarActions(item).filter(action => action.noTable !== true);
    },
    isLoading,
    isFetching,
    dataUpdatedAt,
    getContextType(item: RequirementTableItemData) {
      const dataSource = getTypeConfig(item)?.getAdditionalData?.(
        item,
        context
      );

      return dataSource && "contextType" in dataSource
        ? dataSource.contextType
        : undefined;
    },
    getPolicyType(item: RequirementTableItemData) {
      const dataSource = getTypeConfig(item)?.getAdditionalData?.(
        item,
        context
      );

      return dataSource && "configurationId" in dataSource
        ? selectorPolicyTypesFromMonitor(dataSource)
        : undefined;
    },
    getAdditionalData(item: RequirementTableItemData) {
      return getTypeConfig(item)?.getAdditionalData?.(item, context);
    },
    /**
     * The client's display name for a requirement, off the shared payer-row
     * lookup above — so consumers name the payer without resolving its account
     * id a second time. Empty when the row isn't loaded (or has no name).
     */
    getRequirementPayerName(item: RequirementTableItemData) {
      const payerRow = getPayerRow(item.payerAccountId);

      return payerRow ? selectorSearchRowName(payerRow) : "";
    }
  } as const;
};
